import { createStyles, Input as MuiInput, makeStyles } from "@material-ui/core";
import { InputProps } from "@material-ui/core/Input";
import {
    CountryCode,
    formatIncompletePhoneNumber,
    parseIncompletePhoneNumber,
} from "libphonenumber-js/min";
import * as React from "react";

import { RelodeTheme } from "../";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: { margin: 0, flex: 1 },
            characterLimit: {
                fontSize: 12,
                color: theme.relode.colors.backgroundText.main,
                right: 0,
                bottom: 0,
                position: "absolute",
                marginBottom: -16,
            },
            characterLimitNear: {
                color: theme.relode.colors.backgroundText.dark,
                fontWeight: "bold",
            },
        }),
    { name: "phoneInput" }
);

type PhoneInputProps = InputProps & {
    country?: CountryCode;
};

export const PhoneInput = (props: PhoneInputProps) => {
    const [phone, setPhone] = React.useState<string>("");
    const classes = useStyles({});

    React.useEffect(() => {
        setPhone(props.value as string);
    }, [props.value]);

    const handleChangePhone = (event: React.ChangeEvent<any>) => {
        const { onChange } = props;

        let newValue = parseIncompletePhoneNumber(event.target.value);
        const hasCountryCode = newValue.slice(0, 1) === "1";
        const limit = 10 + (hasCountryCode ? 1 : 0);
        if (newValue.length > limit) return;

        // By default, if a value is something like `"(123)"`
        // then Backspace would only erase the rightmost brace
        // becoming something like `"(123"`
        // which would give the same `"123"` value
        // which would then be formatted back to `"(123)"`
        // and so a user wouldn't be able to erase the phone number.
        // Working around this issue with this simple hack.
        if (newValue === phone) {
            if (format(newValue).indexOf(event.target.value) === 0) {
                // Trim the last digit (or plus sign).
                newValue = newValue.slice(0, -1);
            }
        }

        // Prevents React from resetting the `<input/>` caret position.
        // https://github.com/reactjs/react-redux/issues/525#issuecomment-254852039
        // https://github.com/facebook/react/issues/955
        setPhone(newValue);
        if (onChange) {
            event.target.value = newValue;
            onChange(event);
        }
    };

    const format = (value: string) => {
        const { country = "US" } = props;
        return formatIncompletePhoneNumber(value, country);
    };

    const displayValue = format(!!phone ? phone : "");
    return (
        <MuiInput
            {...props}
            margin="none"
            value={displayValue}
            onChange={handleChangePhone}
            className={classes.root}
            data-cy="phone-number-input"
        />
    );
};
